'use client';

import { useEffect } from 'react';
import { ExternalLink, Music2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PortfolioVideoEntry } from '@/lib/types/portfolio-video';

// Rendu par type de vidéo, factorisé une seule fois — réutilisé par le Hero
// (bande diagonale) et par VideoShowcase (rangée principale).
export function VideoEmbed({
  entry,
  shouldPlay,
  className,
  onReady,
}: {
  entry: PortfolioVideoEntry;
  shouldPlay: boolean;
  className: string;
  // Signale la fin du chargement (iframe chargée / première frame vidéo
  // disponible) pour permettre un fondu d'entrée plutôt qu'un flash brut.
  onReady?: () => void;
}) {
  useEffect(() => {
    if (shouldPlay && entry.type === 'tiktok') onReady?.();
  }, [entry.type, onReady, shouldPlay]);

  if (!shouldPlay) return null;

  switch (entry.type) {
    case 'vimeo':
      return (
        <iframe
          src={`https://player.vimeo.com/video/${entry.id}?background=1&autoplay=1&loop=1&muted=1`}
          className={cn(className, "border-0 scale-[1.4]")}
          allow="autoplay"
          loading="lazy"
          onLoad={onReady}
        />
      );
    case 'mp4':
      return (
        <video
          src={entry.url}
          autoPlay muted loop playsInline preload="none"
          className={className}
          onLoadedData={onReady}
        />
      );
    case 'youtube':
      return (
        <iframe
          src={`https://www.youtube.com/embed/${entry.id}?autoplay=1&mute=1&loop=1&playlist=${entry.id}&controls=0&rel=0&modestbranding=1`}
          className={cn(className, "border-0")}
          allow="autoplay"
          loading="lazy"
          onLoad={onReady}
        />
      );
    case 'tiktok':
      // Le lecteur embarqué TikTok charge des scripts et médias temporaires qui
      // répondent régulièrement 403 et lèvent une exception dans le SDK tiers.
      // Un lien visuel stable conserve l'accès à la vidéo sans polluer la
      // console ni fragiliser le reste de la landing.
      return (
        <a
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Voir la vidéo sur TikTok"
          className={cn(
            className,
            "flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800 text-white"
          )}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10">
            <Music2 className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold">
            Voir sur TikTok <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </span>
        </a>
      );
  }
}
